import { useCallback, useEffect, useState } from "react";
import { BookOpen, UploadSimple, FileArrowDown, Warning } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function TrainingBasePage() {
  const [sources, setSources] = useState(null);
  const [upload, setUpload] = useState({ title: "", tags: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/knowledge/sources")
      .then((r) => setSources((r.data.sources || []).filter((s) => s.source_type === "training_material")))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const doUpload = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", upload.title || file.name.replace(/\.[^.]+$/, ""));
      fd.append("source_type", "training_material");
      fd.append("authority_level", "training_orientation");
      fd.append("jurisdiction", "Pennsylvania");
      fd.append("tags", upload.tags || "training, owner-uploaded");
      await api.post("/knowledge/sources", fd, { timeout: 300000 });
      toast.success("Added to the training base");
      setUpload({ title: "", tags: "" });
      setFile(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8" data-testid="training-base-page">
      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        <img src="/training-art/learning_band.jpeg" alt="" aria-hidden="true" className="h-32 w-full object-cover sm:h-40" />
        <div className="p-6">
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Training base</h1>
          <p className="mt-1 max-w-2xl text-sm text-stone-500">
            The source library Silver Home Care trainings are written from. Upload guides, drafts, and reference
            materials here — every new or revised training is built from what's in this base. General-use facts
            and formats inform our writing; licensed text is never copied.
          </p>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="training-base-upload">
        <h2 className="font-heading text-lg font-semibold text-stone-900">Add a source</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <p className="mb-1 text-xs font-medium text-stone-500">Title (optional — defaults to the file name)</p>
            <Input value={upload.title} onChange={(e) => setUpload((u) => ({ ...u, title: e.target.value }))}
              className="border-stone-300" data-testid="training-base-title" />
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-stone-500">Tags (comma separated, optional)</p>
            <Input value={upload.tags} onChange={(e) => setUpload((u) => ({ ...u, tags: e.target.value }))}
              placeholder="training, orientation" className="border-stone-300" data-testid="training-base-tags" />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-stone-300 px-4 py-2.5 text-sm font-medium text-stone-600 hover:bg-accent" data-testid="training-base-file-label">
            <UploadSimple size={16} /> {file ? file.name : "Choose file (PDF, Word, text)"}
            <input type="file" className="hidden" accept=".pdf,.docx,.doc,.txt,.md,.xlsx,.csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="training-base-file" />
          </label>
          <Button onClick={doUpload} disabled={busy || !file} className="rounded-full bg-primary text-primary-foreground" data-testid="training-base-upload-btn">
            {busy ? "Uploading…" : "Add to training base"}
          </Button>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="training-base-list">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900">
          <BookOpen size={18} className="text-primary" /> Sources in the base {sources && <span className="text-sm font-normal text-stone-400">({sources.length})</span>}
        </h2>
        {!sources ? (
          <p className="mt-4 text-sm text-stone-400">Loading…</p>
        ) : sources.length === 0 ? (
          <p className="mt-4 text-sm text-stone-500">Nothing here yet — add the first source above.</p>
        ) : (
          <div className="mt-3 divide-y divide-stone-100">
            {sources.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`training-base-source-${s.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-stone-800">{s.title}</p>
                  <p className="mt-0.5 text-xs text-stone-400">
                    v{s.version} · added {String(s.created_at || "").slice(0, 10)}
                    {s.page_count ? ` · ${s.page_count} pages` : ""}
                    {(s.tags || []).length > 0 && ` · ${s.tags.join(", ")}`}
                  </p>
                  {s.extraction_status === "ocr_needed" && (
                    <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-[#B7791F]" data-testid={`training-base-ocr-${s.id}`}>
                      <Warning size={13} /> Scanned document — text not yet searchable
                    </p>
                  )}
                </div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/knowledge/sources/${s.id}/file`} target="_blank" rel="noreferrer"
                  className="flex shrink-0 items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-medium text-stone-600 hover:bg-accent"
                  data-testid={`training-base-open-${s.id}`}>
                  <FileArrowDown size={14} /> Open
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
